import { Box, Dialog, DialogActions, DialogContent, Divider, Typography } from "@mui/material"
import dayjs from "dayjs"
import type { FC } from "react"

import { Button } from "@/components/common/Button"
import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import { DATE_FORMAT } from "@/constants/formats"
import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { ProductTestDocumentsTable } from "@/screens/Products/components/Product/Quality/TestResults/components/ProductTestDocumentsTable"

interface InventoryAdjustmentHistoryDocumentsDialogProps {
  item: AdjustmentHistoryItem
  onClose: () => void
}

export const InventoryAdjustmentHistoryDocumentsDialog: FC<InventoryAdjustmentHistoryDocumentsDialogProps> = ({
  item,
  onClose,
}) => {
  const { fileList } = useGetFileUploadData({
    referenceId: item.inventoryAdjustmentId,
    referenceTableName: ReferenceTableName.InventoryAdjustment,
  })

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogHeader title='Documents' onClose={onClose} />
      <DialogContent>
        <Box className='flex flex-col gap-6 px-5 py-3'>
          <Box className='flex flex-col gap-3'>
            <Typography className='text-base font-bold text-gray-700'>
              {item.description.akrochemDescription}
            </Typography>
            <Box className='flex items-start gap-10'>
              <Box className='flex flex-col gap-1'>
                <Typography className='text-sm font-bold text-gray-700'>Product ID</Typography>
                <Typography variant='body2' className='text-gray-700'>
                  {item.productPublicId ?? item.productId}
                </Typography>
                {item.productExternalSystemId && <DisplayAS400Id id={item.productExternalSystemId} />}
              </Box>
              <Box className='flex flex-col gap-1'>
                <Typography className='text-sm font-bold text-gray-700'>Date</Typography>
                <Typography variant='body2' className='text-gray-700'>
                  {dayjs(item.date).format(DATE_FORMAT.US)}
                </Typography>
              </Box>
              <Box className='flex flex-col gap-1'>
                <Typography className='text-sm font-bold text-gray-700'>Adjustment #</Typography>
                <Typography variant='body2' className='text-gray-700'>
                  {item.adjustmentReferenceNbr}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box className='flex flex-col gap-2.5'>
            <Typography className='text-base font-medium text-gray-700'>View Documents</Typography>
            <Divider />
          </Box>

          <ProductTestDocumentsTable data={fileList} canDelete={false} onDelete={() => {}} />
        </Box>
      </DialogContent>

      <DialogActions className='flex items-center justify-end px-5 pb-5'>
        <Button appearance='contained' onClick={onClose} data-testid='adjustment-history.documents-dialog.close-button'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
